import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import api from '@/lib/api';

const BUILD_ENABLED = process.env.REACT_APP_NATIVE_PUSH_ENABLED === 'true';
const ENVIRONMENT = process.env.REACT_APP_APNS_ENVIRONMENT || 'sandbox';
let context = null;
let handles = [];
let pending = null;
let initialization;
const preferenceKey = (id) => `ca_native_push_optin_${id}`;

export const isNativePush = () => Capacitor.isNativePlatform();

function installationId() {
  const key = 'ca_native_installation_id';
  let id = localStorage.getItem(key);
  if (!id) { id = crypto.randomUUID(); localStorage.setItem(key, id); }
  return id;
}

export async function nativePushAvailable() {
  if (!isNativePush() || !BUILD_ENABLED) return false;
  const { data } = await api.get('/push/native/status');
  return Boolean(data[Capacitor.getPlatform()]);
}

export async function nativePushPermission() {
  if (!BUILD_ENABLED) return 'unavailable';
  const { receive } = await PushNotifications.checkPermissions();
  return receive === 'prompt' || receive === 'prompt-with-rationale' ? 'default' : receive;
}

async function listeners() {
  if (initialization) return initialization;
  initialization = (async () => {
    handles.push(await PushNotifications.addListener('registration', async ({ value }) => {
      const current = context;
      if (!current || (!pending && localStorage.getItem(preferenceKey(current.user.id)) !== 'true')) return;
      try {
        const { data } = await api.post('/push/native/register', {
          token: value, platform: Capacitor.getPlatform(), environment: ENVIRONMENT,
          installation_id: installationId(), device_label: current.label || 'Teléfono C&A',
        }, { timeout: 10000 });
        if (context !== current) return;
        localStorage.setItem('ca_native_push_device', data.id);
        localStorage.setItem(preferenceKey(current.user.id), 'true');
        pending?.resolve({ ok: true, endpoint: data.id });
      } catch (error) {
        pending?.reject(new Error(error?.response?.data?.detail || 'No se pudo registrar el teléfono.'));
      }
    }));
    handles.push(await PushNotifications.addListener('registrationError', () => {
      pending?.reject(new Error('No se pudo activar las alertas. Inténtalo nuevamente.'));
    }));
    handles.push(await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      if (!context) return;
      const role = context.user.role;
      const serviceId = notification.data?.service_id;
      const operations = ['admin', 'super_admin', 'dispatcher', 'customer_service'].includes(role);
      const route = role === 'driver' ? '/driver'
        : operations && /^[A-Za-z0-9_-]{1,100}$/.test(serviceId || '')
          ? `/service/${serviceId}` : context.user.home_route || '/app';
      context.onOpen(route);
    }));
  })().catch(async (error) => {
    await Promise.all(handles.splice(0).map((handle) => handle.remove()));
    initialization = undefined;
    throw error;
  });
  return initialization;
}

export async function bindNativePush(user, onOpen) {
  if (!isNativePush() || !BUILD_ENABLED) return;
  const current = { user, onOpen };
  context = current;
  await listeners();
  if (context !== current || localStorage.getItem(preferenceKey(user.id)) !== 'true') return;
  if (await nativePushAvailable() && await nativePushPermission() === 'granted' && context === current) {
    await PushNotifications.register();
  }
}

export function unbindNativePush() {
  context = null;
  pending?.reject(new Error('La sesión cambió.'));
}

export async function enrollNativePush(label) {
  if (!context || !(await nativePushAvailable())) {
    throw new Error('Las alertas para este dispositivo todavía no están disponibles.');
  }
  if (pending) throw new Error('Espera a que termine el registro.');
  context.label = label;
  await listeners();
  let permission = await PushNotifications.checkPermissions();
  if (permission.receive !== 'granted') permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') throw new Error('Activa las notificaciones en los ajustes del teléfono.');
  if (Capacitor.getPlatform() === 'android') {
    await PushNotifications.createChannel({ id: 'ca-operations', name: 'Servicios C&A', importance: 4 });
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => pending?.reject(new Error('El registro tardó demasiado. Inténtalo nuevamente.')), 20000);
    pending = {
      resolve: (value) => { clearTimeout(timer); pending = null; resolve(value); },
      reject: (error) => { clearTimeout(timer); pending = null; reject(error); },
    };
    PushNotifications.register().catch(() => pending?.reject(new Error('No se pudo activar las alertas.')));
  });
}

export async function stopNativePush({ logout = false } = {}) {
  if (!isNativePush()) return;
  const previous = context;
  const userId = previous?.user.id;
  unbindNativePush();
  if (userId) localStorage.removeItem(preferenceKey(userId));
  const id = localStorage.getItem('ca_native_push_device');
  localStorage.removeItem('ca_native_push_device');
  if (id) await api.delete(`/push/devices?endpoint=${encodeURIComponent(id)}`, { timeout: 5000 }).catch(() => {});
  if (BUILD_ENABLED) {
    await PushNotifications.unregister();
    await PushNotifications.removeAllDeliveredNotifications();
  }
  if (!logout) context = previous;
}
